import React, { useEffect, useState } from "react";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  TextField,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSettings from "../../hooks/useSettings";
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: theme.spacing(2),
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
    width: "100%",
    "& .MuiTab-wrapper": {
      color: theme.palette.fontecor,
    },
    "& .MuiTabs-flexContainer": {
      justifyContent: "center",
    },
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
}));

// Componente reutilizável
const SelectOption = ({ label, value, onChange, loading, options }) => (
  <FormControl className={useStyles().selectContainer} fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
    <FormHelperText>{loading && "Atualizando..."}</FormHelperText>
  </FormControl>
);

// Opções de configuração
const OPTIONS = {
  userRating: [
    { value: "disabled", label: "Desabilitadas" },
    { value: "enabled", label: "Habilitadas" },
  ],
  scheduleType: [
    { value: "disabled", label: "Desabilitado" },
    { value: "queue", label: "Fila" },
    { value: "company", label: "Empresa" },
  ],
  call: [
    { value: "disabled", label: "Não Aceitar" },
    { value: "enabled", label: "Aceitar" },
  ],
  CheckMsgIsGroup: [
    { value: "disabled", label: "Desativado" },
    { value: "enabled", label: "Ativado" },
  ],
  sendGreetingAccepted: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  sendMsgTransfTicket: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  chatBotType: [
    { value: "text", label: "Texto" },
  ],
};

// Componente TabPanel (reutilizável)
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`options-tabpanel-${index}`}
      aria-labelledby={`options-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

export default function Options({ settings, scheduleTypeChanged }) {
  const classes = useStyles();
  const { update } = useSettings();

  const [tabIndex, setTabIndex] = useState(0);
  const [settingsState, setSettingsState] = useState({});
  const [loading, setLoading] = useState({});

  // Carrega os valores iniciais
  useEffect(() => {
    if (Array.isArray(settings)) {
      const initial = {};
      settings.forEach((s) => {
        if (s.key) initial[s.key] = s.value;
      });
      setSettingsState(initial);

      if (typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(initial.scheduleType === "company");
      }
    }
  }, [settings, scheduleTypeChanged]);

  // Função genérica de atualização
  const updateSetting = async (key, value, loaderKey = key) => {
    setLoading((prev) => ({ ...prev, [loaderKey]: true }));
    try {
      await update({ key, value });
      setSettingsState((prev) => ({ ...prev, [key]: value }));
      toast.success("Operação atualizada com sucesso.");

      if (key === "scheduleType" && typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(value === "company");
      }
    } catch (err) {
      toast.error("Erro ao atualizar configuração.");
    } finally {
      setLoading((prev) => ({ ...prev, [loaderKey]: false }));
    }
  };

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      {/* Abas internas */}
      <Tabs
        value={tabIndex}
        onChange={handleChangeTab}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tab}
      >
        <Tab label="Geral" />
        <Tab label="Integrações" />
      </Tabs>

      {/* Painel da aba "Geral" */}
      <TabPanel value={tabIndex} index={0}>
        <Typography variant="h6" gutterBottom>Configurações Gerais</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Avaliações"
              value={settingsState.userRating || "disabled"}
              onChange={(value) => updateSetting("userRating", value)}
              loading={loading.userRating}
              options={OPTIONS.userRating}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Expediente"
              value={settingsState.scheduleType || "disabled"}
              onChange={(value) => updateSetting("scheduleType", value)}
              loading={loading.scheduleType}
              options={OPTIONS.scheduleType}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Ignorar Mensagens de Grupos"
              value={settingsState.CheckMsgIsGroup || "disabled"}
              onChange={(value) => updateSetting("CheckMsgIsGroup", value)}
              loading={loading.CheckMsgIsGroup}
              options={OPTIONS.CheckMsgIsGroup}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Aceitar Chamada"
              value={settingsState.call || "disabled"}
              onChange={(value) => updateSetting("call", value)}
              loading={loading.call}
              options={OPTIONS.call}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Chatbot"
              value={settingsState.chatBotType || "text"}
              onChange={(value) => updateSetting("chatBotType", value)}
              loading={loading.chatBotType}
              options={OPTIONS.chatBotType}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Saudação ao aceitar"
              value={settingsState.sendGreetingAccepted || "disabled"}
              onChange={(value) => updateSetting("sendGreetingAccepted", value)}
              loading={loading.sendGreetingAccepted}
              options={OPTIONS.sendGreetingAccepted}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SelectOption
              label="Mensagem de transferência"
              value={settingsState.sendMsgTransfTicket || "disabled"}
              onChange={(value) => updateSetting("sendMsgTransfTicket", value)}
              loading={loading.sendMsgTransfTicket}
              options={OPTIONS.sendMsgTransfTicket}
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Painel da aba "Integrações" */}
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="h6" gutterBottom>Integrações Externas</Typography>

        {/* IXC */}
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="subtitle1" gutterBottom>IXC</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="IP do IXC"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.ipixc || ""}
                onChange={(e) => updateSetting("ipixc", e.target.value, "ipixc")}
                InputProps={{
                  endAdornment: loading.ipixc && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Token do IXC"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.tokenixc || ""}
                onChange={(e) => updateSetting("tokenixc", e.target.value, "tokenixc")}
                InputProps={{
                  endAdornment: loading.tokenixc && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* MK-AUTH */}
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="subtitle1" gutterBottom>MK-AUTH</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="IP Mk-Auth"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.ipmkauth || ""}
                onChange={(e) => updateSetting("ipmkauth", e.target.value, "ipmkauth")}
                InputProps={{
                  endAdornment: loading.ipmkauth && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Client Id"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.clientidmkauth || ""}
                onChange={(e) => updateSetting("clientidmkauth", e.target.value, "clientidmkauth")}
                InputProps={{
                  endAdornment: loading.clientidmkauth && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Client Secret"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.clientsecretmkauth || ""}
                onChange={(e) => updateSetting("clientsecretmkauth", e.target.value, "clientsecretmkauth")}
                InputProps={{
                  endAdornment: loading.clientsecretmkauth && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ASAAS */}
        <Paper variant="outlined" style={{ padding: 16 }}>
          <Typography variant="subtitle1" gutterBottom>ASAAS</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Token Asaas"
                variant="outlined"
                margin="dense"
                fullWidth
                value={settingsState.asaas || ""}
                onChange={(e) => updateSetting("asaas", e.target.value, "asaas")}
                InputProps={{
                  endAdornment: loading.asaas && (
                    <FormHelperText>Atualizando...</FormHelperText>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>
    </Paper>
  );
}